import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import * as d3 from 'd3';
import React from 'react';
import {noop} from 'underscore';

import {NucleotideType} from 'biojs/BioConstants';
import {SequenceType} from 'constants/SequenceConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {COMPLEX_POLYMER_FAKE_SEQUENCE_ID} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  AXIS_HEIGHT,
  AxisOrientation,
  FONT_SIZE,
  getWidthForBaseCount,
  LETTER_SPACING,
  StaticAxis,
  StaticBases,
} from 'modbio/polymer-maps/OligoMap';
import {
  ModifiedNucleotidesVisualization,
  modifiedNucleotideVisualizationHeight,
  modifiedNucleotideVMargin,
  PHOSPHATE_WIDTH,
} from 'molbio/maps/linear/ModifiedNucleotides';
import Type from 'molbio/maps/Type';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {getDisplayModsForSlice} from 'molbio/monomers/ModificationVisualizationUtil';
import {
  createLegendItems,
  LegendItem,
  ModifiedNucleotidesLegendContentsFromItems,
} from 'molbio/monomers/ModifiedNucleotidesLegend';
import {MONOMER_QUERY} from 'molbio/monomers/MonomerConstants';
import {ModifiedNucleotide} from 'molbio/monomers/NucleotidesTable';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import {isValidOligoDuplexOffset} from '../ComplexPolymerUtil';
import {
  OligoDuplexOligoMapQuery,
  OligoDuplexOligoMapQueryVariables,
} from './__generated/OligoDuplexOligoMapQuery';
import {ELEMENT_MARGIN, getOligoStrandLeftPadding} from './ComplexPolymerMapUtil';

type Monomer = MonomerQuery['monomers'][number];
type SequenceGraphQL = ExtractByTypename<OligoDuplexOligoMapQuery['sequences'][number], 'SequenceNode'>;
interface OligoDuplexOligo {
  bases: string;
  modifiedNucleotides: Array<ModifiedNucleotide>;
  nucleotideType: NucleotideType;
}
interface OligoDuplexOligoStrand {
  oligo: OligoDuplexOligo;
}

export const TRANSLATION_WIDTH = 2;

export const OLIGO_DUPLEX_OLIGO_MAP_QUERY = gql`
  query OligoDuplexOligoMapQuery($ids: [StrictString]!) @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    sequences(ids: $ids) {
      ... on SequenceNode {
        id
        bases
        nucleotideType
        modifiedNucleotides {
          sugarMonomerId
          baseMonomerId
          phosphateMonomerId
          position
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function getLegendItemsForStrand(oligo: OligoDuplexOligo, monomers: Array<Monomer>): Array<LegendItem> {
  const displayMods = getDisplayModsForSlice(
    monomers,
    oligo.nucleotideType,
    oligo.bases,
    oligo.modifiedNucleotides,
    0
  );
  return createLegendItems(displayMods, monomers, oligo.nucleotideType, true);
}

export function getOligoDuplexLegendItems(
  forwardOligo: OligoDuplexOligo,
  reverseOligo: OligoDuplexOligo,
  monomers: Array<Monomer>
): Array<LegendItem> {
  const forwardStrandLegendItems = getLegendItemsForStrand(forwardOligo, monomers);
  const reverseStrandLegendItems = getLegendItemsForStrand(reverseOligo, monomers);

  const seenLegendItems = new Set();
  const uniqueLegendItems: Array<LegendItem> = [];
  [...forwardStrandLegendItems, ...reverseStrandLegendItems].forEach((legendItem) => {
    if (!seenLegendItems.has(legendItem.key)) {
      uniqueLegendItems.push(legendItem);
      seenLegendItems.add(legendItem.key);
    }
  });
  return uniqueLegendItems;
}
export interface QueryingOligoDuplexOligoMapProps {
  oligoIds: Array<string>;
  offset: number;
}

/**
 * Component that displays only the backbone oligos of an oligo duplex. This does NOT display conjugates and
 * should only be used for an oligo duplex that does not have associated structure data, for example, if it
 * has not yet been created.
 */
export function QueryingOligoDuplexOligoMap({
  oligoIds,
  offset,
}: QueryingOligoDuplexOligoMapProps): JSX.Element {
  return (
    <BenchlingQuery<OligoDuplexOligoMapQuery, OligoDuplexOligoMapQueryVariables>
      query={OLIGO_DUPLEX_OLIGO_MAP_QUERY}
      renderLoading={() => {
        <div className={css(styles.emptyOligoMap)}>
          <HorizontalThrobber />
        </div>;
      }}
      variables={{ids: oligoIds}}
    >
      {(data) => {
        const oligosOrInaccessible = data.sequences;
        if (oligosOrInaccessible.find((oligo) => isInaccessible(oligo))) {
          return (
            <div className={css(styles.emptyOligoMap)}>
              You do not have access to view the selected oligo(s)
            </div>
          );
        }

        const oligos = oligosOrInaccessible.filter(
          (oligoOrInaccessible): oligoOrInaccessible is SequenceGraphQL =>
            !isInaccessible(oligoOrInaccessible)
        );
        const [forwardOligo, reverseOligo] = oligos;
        // The reverse strand should be displayed left to right, 3' to 5' so we reverse the bases
        // and adjust the positions of the modified nucleotides
        const adjustedReverseOligo = {
          ...reverseOligo,
          bases: reverseOligo.bases.split('').reverse().join(''),
          modifiedNucleotides: reverseOligo.modifiedNucleotides.map((mod) => ({
            ...mod,
            position: reverseOligo.bases.length - 1 - mod.position,
          })),
        };

        if (forwardOligo.bases.length === 0 || reverseOligo.bases.length === 0) {
          return (
            <div className={css(styles.emptyOligoMap)}>
              Structure cannot be previewed because at least one of the selected oligo(s) has no bases
            </div>
          );
        }

        if (!isValidOligoDuplexOffset(offset, forwardOligo.bases.length, reverseOligo.bases.length)) {
          return (
            <div className={css(styles.emptyOligoMap)}>
              Structure cannot be previewed because the selected oligos do not overlap with the provided
              offset
            </div>
          );
        }

        const forwardStrand = {oligo: forwardOligo};
        const reverseStrand = {oligo: adjustedReverseOligo};

        return (
          <OligoDuplexPolymerMap
            forwardStrand={forwardStrand}
            reverseStrand={reverseStrand}
            offset={offset}
            forCreate={true}
          />
        );
      }}
    </BenchlingQuery>
  );
}

export interface OligoDuplexPolymerMapProps {
  forwardStrand: OligoDuplexOligoStrand;
  reverseStrand: OligoDuplexOligoStrand;
  offset: number;
  forCreate: boolean;
}

interface StrandTransform {
  axisYTransform: number;
  basesYTransform: number;
  xTransform: number;
}

function getStrand(
  polymer: OligoDuplexOligoStrand,
  isForward: boolean,
  strandTransform: StrandTransform,
  type: Type
): JSX.Element {
  const {bases, modifiedNucleotides} = polymer.oligo;
  const basesLength = bases.length;

  const basesWidth = getWidthForBaseCount(basesLength);
  const xFn = d3.scaleLinear().clamp(true).domain([0, basesLength]).range([0, basesWidth]);
  const axisxFn = isForward
    ? xFn
    : d3.scaleLinear().clamp(true).domain([0, basesLength]).range([basesWidth, 0]);

  return (
    <>
      <g
        id={`${isForward ? 'forward' : 'reverse'}Bases`}
        transform={`translate(${strandTransform.xTransform},${strandTransform.basesYTransform})`}
      >
        <BenchlingQuery<MonomerQuery>
          query={MONOMER_QUERY}
          renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
        >
          {(monomerData) => (
            <ModifiedNucleotidesVisualization
              bases={bases}
              sequenceMods={modifiedNucleotides}
              monomers={monomerData.monomers}
              isOligo={true}
              nucleotideType={polymer.oligo.nucleotideType}
              xFn={xFn}
              sliceStart={0}
              sliceEnd={basesLength}
              layout={{vMargin: 0}}
              boxHeightToDNA={0}
              showNucleotideStructures={true}
              keyPrefix="complexPolymerMap"
              displayBasesAsNatural={false}
              isForwardStrand={isForward}
              // Return as G rather than SVG so we can translate the entire polymer map to the right, and
              // ensure that the entire phosphate visualization is shown.
              shouldReturnG={true}
            />
          )}
        </BenchlingQuery>
        <StaticBases
          bases={bases}
          modifiedNucleotides={modifiedNucleotides}
          nucleotideType={polymer.oligo.nucleotideType}
          sequenceType={SequenceType.OLIGO}
          fontSize={type.fontSize}
          letterSpacing={type.letterSpacing}
          vMargin={10}
          isTemplate={false}
        />
      </g>
      <g
        id={`${isForward ? 'forward' : 'reverse'}Axis`}
        transform={`translate(${strandTransform.xTransform},${strandTransform.axisYTransform})`}
      >
        <StaticAxis
          basesLength={basesLength}
          charWidth={type.charWidth()}
          xFn={axisxFn}
          vMargin={isForward ? AXIS_HEIGHT : 0}
          tickSpacing={2}
          axisOrientation={isForward ? AxisOrientation.TOP : AxisOrientation.BOTTOM}
          isReverse={!isForward}
        />
      </g>
    </>
  );
}

export default function OligoDuplexPolymerMap({
  forwardStrand,
  reverseStrand,
  offset,
  forCreate,
}: OligoDuplexPolymerMapProps): JSX.Element {
  const forwardBasesWidth = getWidthForBaseCount(forwardStrand.oligo.bases.length);
  const forwardOffsetWidth = getWidthForBaseCount(getOligoStrandLeftPadding(offset, true));
  const reverseBasesWidth = getWidthForBaseCount(reverseStrand.oligo.bases.length);
  const reverseOffsetWidth = getWidthForBaseCount(getOligoStrandLeftPadding(offset, false));

  // The total width of the container is the maximum of each strand's width, where a strand's width is the sum
  // of any horizontal offset, the width of all bases in the strand, the width of any trailing phosphate/chip
  const width = Math.max(
    forwardBasesWidth + forwardOffsetWidth + PHOSPHATE_WIDTH + TRANSLATION_WIDTH,
    reverseBasesWidth + reverseOffsetWidth + PHOSPHATE_WIDTH + TRANSLATION_WIDTH
  );

  // The base height and vmargin are functions of the width between letters which is equivalent for both forward
  // and reverse strands, so we can use either strand's xFn here to determine these values
  const xFn = d3
    .scaleLinear()
    .clamp(true)
    .domain([0, forwardStrand.oligo.bases.length])
    .range([0, forwardBasesWidth]);
  const basesHeight = modifiedNucleotideVisualizationHeight(xFn, 0, 0);
  const basesVMargin = modifiedNucleotideVMargin(xFn, 0, 0);

  const forwardBasesYTransform = AXIS_HEIGHT + ELEMENT_MARGIN - basesVMargin;
  const reverseBasesYTransform = forwardBasesYTransform + basesHeight + ELEMENT_MARGIN - basesVMargin;
  const forwardAxisYTransform = forwardBasesYTransform - ELEMENT_MARGIN - AXIS_HEIGHT + basesVMargin;
  const reverseAxisYTransform = reverseBasesYTransform + basesHeight + ELEMENT_MARGIN;
  const polymerSvgHeight = reverseAxisYTransform + AXIS_HEIGHT;

  const forwardStrandTransform = {
    basesYTransform: forwardBasesYTransform,
    axisYTransform: forwardAxisYTransform,
    xTransform: forwardOffsetWidth,
  };
  const reverseStrandTransform = {
    basesYTransform: reverseBasesYTransform,
    axisYTransform: reverseAxisYTransform,
    xTransform: reverseOffsetWidth,
  };

  const type = new Type(FONT_SIZE, LETTER_SPACING);

  const forwardOligo = forwardStrand.oligo;
  const reverseOligo = reverseStrand.oligo;

  return (
    <div {...(!forCreate && {className: css(styles.oligoMapWithLegend)})}>
      <div
        className={
          forCreate ? classNames(css(styles.wrapper), css(styles.oligoMapForCreate)) : css(styles.wrapper)
        }
        {...getTestProps({component: 'OligoDuplexPolymerMap', element: 'root'})}
      >
        <svg style={{height: polymerSvgHeight, width}}>
          <g transform={`translate(${TRANSLATION_WIDTH}, 0)`}>
            {getStrand(forwardStrand, true, forwardStrandTransform, type)}
            {getStrand(reverseStrand, false, reverseStrandTransform, type)}
          </g>
        </svg>
      </div>
      <div className={forCreate ? css(styles.legend) : classNames(css(styles.legend, styles.legendMaxWidth))}>
        <BenchlingQuery<MonomerQuery> query={MONOMER_QUERY} renderLoading={({refetch}) => null}>
          {({monomers}) => {
            const uniqueLegendItems = getOligoDuplexLegendItems(forwardOligo, reverseOligo, monomers);
            return (
              <ModifiedNucleotidesLegendContentsFromItems
                // The sequence ID would be used to query for the oligo structure if needed, but since we provide
                // the structure here, the sequence ID will not be used
                sequenceId={COMPLEX_POLYMER_FAKE_SEQUENCE_ID}
                legendItems={uniqueLegendItems}
                onUpdateHoverState={noop}
              />
            );
          }}
        </BenchlingQuery>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    fontFamily: FONT_FAMILY_MONOSPACED,
    shapeRendering: 'crispEdges',
  },
  oligoMapForCreate: {
    height: '200px',
    border: `1px solid ${Color.GRAY_300}`,
    borderRadius: '4px',
    overflowX: 'scroll',
    padding: '35px 15px 0px 15px',
    marginBottom: '10px',
    marginTop: '10px',
  },
  legend: {
    marginLeft: '-20px',
    width: '100%',
  },
  legendMaxWidth: {
    maxWidth: '600px',
  },
  emptyOligoMap: {
    height: '200px',
    border: `1px solid ${Color.GRAY_50}`,
    borderRadius: '4px',
    marginBottom: '10px',
    marginTop: '10px',
    background: Color.GRAY_50,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: Color.GRAY_500,
  },
  oligoMapWithLegend: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: 'fit-content',
  },
});
